import React, { useEffect, useState, useCallback } from "react";
import {
  Card,
  CardHeader,
  CardBody,
  Container,
  Row,
  Col,
  Table,
  Badge,
  Collapse,
  Button,
} from "reactstrap";
import { postMethod } from "services/httpServices";
import { useToasts } from "react-toast-notifications";
import moment from "moment";
import { getAmt } from "services/util";
import FullPageLoader from "components/FullPageLoader/fullpageloader";
import NoRecordFound from "components/NoRecordFound/NoRecordFound";
import jsPDF from "jspdf";
import "jspdf-autotable";
import * as FileSaver from "file-saver";
import * as XLSX from "xlsx";

const DisbursementReport = () => {
  const [reportData, setReportData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expandedMonths, setExpandedMonths] = useState({});
  const [pdfLoading, setPdfLoading] = useState(false);
  const [excelLoading, setExcelLoading] = useState(false);
  const { addToast } = useToasts();

  const fetchReportData = useCallback(async () => {
    setLoading(true);
    try {
      const response = await postMethod(
        "/api/Transactions/GetReportByMonth",
        {}
      );

      if (response && response.data) {
        if (response.data.responseCode === "200" && response.data.result) {
          setReportData(response.data.result || []);
        } else {
          addToast(
            response.data.responseMessage ||
              "Error fetching disbursement report",
            {
              appearance: "error",
            }
          );
        }
      } else {
        addToast("Error fetching disbursement report", {
          appearance: "error",
        });
      }
    } catch (error) {
      addToast("Error fetching disbursement report", {
        appearance: "error",
      });
    } finally {
      setLoading(false);
    }
  }, [addToast]);

  useEffect(() => {
    fetchReportData();
  }, [fetchReportData]);

  const getStatusBadge = (status) => {
    const statusConfig = {
      "Wallet is available": { color: "success", text: "Success" },
      "Wallet not available": { color: "danger", text: "Failed" },
      Processing: { color: "warning", text: "Processing" },
      Pending: { color: "info", text: "Pending" },
    };

    const config = statusConfig[status] || { color: "secondary", text: status };
    return <Badge color={config.color}>{config.text}</Badge>;
  };

  const toggleMonthExpansion = (month) => {
    setExpandedMonths((prev) => ({
      ...prev,
      [month]: !prev[month],
    }));
  };

  const groupDataByMonth = (data) => {
    const grouped = {};
    data.forEach((item) => {
      if (!grouped[item.month]) {
        grouped[item.month] = [];
      }
      grouped[item.month].push(item.log);
    });
    return grouped;
  };

  const getMonthTotals = (monthData) => {
    const totalAmount = monthData.reduce(
      (sum, item) => sum + (item.transferedAmount || 0),
      0
    );
    const totalTransactions = monthData.length;
    return { totalAmount, totalTransactions };
  };

  const groupedData = groupDataByMonth(reportData);

  // Export functions
  const exportToPDF = () => {
    setPdfLoading(true);
    const unit = "pt";
    const size = "A4";
    const orientation = "landscape";

    const marginLeft = 10;
    const doc = new jsPDF(orientation, unit, size);

    doc.setFontSize(15);
    doc.text("Disbursement Report by Month", marginLeft, 40);

    // Prepare data for PDF
    const allTransactions = reportData.map((item) => item.log);
    const headers = [
      "Transaction ID",
      "Account Number",
      "Name",
      "Product",
      "Amount",
      "Wallet Number",
      "Status",
      "Date",
      "Log By",
    ];

    const data = allTransactions.map((transaction) => [
      transaction.transactionId,
      transaction.accountNumber,
      transaction.name,
      transaction.product,
      getAmt(transaction.transferedAmount),
      transaction.walletNumber,
      transaction.status,
      moment(transaction.logDate).format("DD/MM/YYYY HH:mm"),
      transaction.logBy,
    ]);

    const content = {
      startY: 50,
      head: [headers],
      body: data,
      margin: { left: 10, right: 10 },
      styles: { fontSize: 8 },
      headStyles: { fillColor: [41, 128, 185] },
    };

    doc.autoTable(content);
    doc.save("disbursement-report.pdf");

    setTimeout(() => {
      setPdfLoading(false);
    }, 1000);
  };

  const exportToExcel = () => {
    setExcelLoading(true);

    // Prepare data for Excel
    const allTransactions = reportData.map((item) => item.log);

    const data = allTransactions.map((transaction) => ({
      "Transaction ID": transaction.transactionId,
      "Account Number": transaction.accountNumber,
      Name: transaction.name,
      Product: transaction.product,
      Amount: transaction.transferedAmount,
      "Wallet Number": transaction.walletNumber,
      Status: transaction.status,
      Date: moment(transaction.logDate).format("DD/MM/YYYY HH:mm"),
      "Log By": transaction.logBy,
    }));

    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(data);

    // Set column widths
    const wscols = [
      { wch: 15 }, // Transaction ID
      { wch: 15 }, // Account Number
      { wch: 25 }, // Name
      { wch: 15 }, // Product
      { wch: 12 }, // Amount
      { wch: 15 }, // Wallet Number
      { wch: 20 }, // Status
      { wch: 18 }, // Date
      { wch: 25 }, // Log By
    ];
    ws["!cols"] = wscols;

    XLSX.utils.book_append_sheet(wb, ws, "Disbursement Report");

    const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    const dataBlob = new Blob([excelBuffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });

    FileSaver.saveAs(dataBlob, "disbursement-report.xlsx");

    setTimeout(() => {
      setExcelLoading(false);
    }, 1000);
  };

  return (
    <>
      <div className="header bg-gradient-info pb-8 pt-5 pt-md-8">
        <Container fluid>
          <div className="header-body">
            <Row className="justify-content-center">
              <Col lg="4" xl="4">
                <Card className="card-stats mb-4 mb-xl-0">
                  <CardBody>
                    <Row>
                      <div className="col">
                        <h5 className="card-title text-uppercase text-muted mb-0">
                          Total Months
                        </h5>
                        <span className="h2 font-weight-bold mb-0">
                          {Object.keys(groupedData).length}
                        </span>
                      </div>
                      <Col className="col-auto">
                        <div className="icon icon-shape bg-danger text-white rounded-circle shadow">
                          <i className="fas fa-calendar" />
                        </div>
                      </Col>
                    </Row>
                  </CardBody>
                </Card>
              </Col>
              <Col lg="4" xl="4">
                <Card className="card-stats mb-4 mb-xl-0">
                  <CardBody>
                    <Row>
                      <div className="col">
                        <h5 className="card-title text-uppercase text-muted mb-0">
                          Total Transactions
                        </h5>
                        <span className="h2 font-weight-bold mb-0">
                          {reportData.length}
                        </span>
                      </div>
                      <Col className="col-auto">
                        <div className="icon icon-shape bg-warning text-white rounded-circle shadow">
                          <i className="fas fa-exchange-alt" />
                        </div>
                      </Col>
                    </Row>
                  </CardBody>
                </Card>
              </Col>
              <Col lg="4" xl="4">
                <Card className="card-stats mb-4 mb-xl-0">
                  <CardBody>
                    <Row>
                      <div className="col">
                        <h5 className="card-title text-uppercase text-muted mb-0">
                          Total Amount
                        </h5>
                        <span className="h2 font-weight-bold mb-0">
                          {getAmt(
                            reportData.reduce(
                              (sum, item) =>
                                sum + (item.log?.transferedAmount || 0),
                              0
                            )
                          )}
                        </span>
                      </div>
                      <Col className="col-auto">
                        <div className="icon icon-shape bg-success text-white rounded-circle shadow">
                          <i className="fas fa-money-bill" />
                        </div>
                      </Col>
                    </Row>
                  </CardBody>
                </Card>
              </Col>
            </Row>
          </div>
        </Container>
      </div>

      <Container className="mt--7" fluid>
        <Row>
          <div className="col">
            <Card className="shadow">
              <CardHeader className="bg-transparent d-flex justify-content-between align-items-center">
                <h3 className="mb-0">Disbursement Report by Month</h3>
                <div>
                  <Button
                    color="danger"
                    size="sm"
                    onClick={exportToPDF}
                    disabled={pdfLoading || reportData.length === 0}
                    className="mr-2"
                  >
                    {pdfLoading ? "Generating..." : "Export PDF"}
                  </Button>
                  <Button
                    color="success"
                    size="sm"
                    onClick={exportToExcel}
                    disabled={excelLoading || reportData.length === 0}
                  >
                    {excelLoading ? "Generating..." : "Export Excel"}
                  </Button>
                </div>
              </CardHeader>
              <CardBody>
                <div className="table-responsive">
                  {Object.keys(groupedData).map((month) => {
                    const monthData = groupedData[month];
                    const { totalAmount, totalTransactions } =
                      getMonthTotals(monthData);
                    const isExpanded = expandedMonths[month];

                    return (
                      <div key={month} className="mb-4">
                        <Card
                          className="shadow-sm"
                          style={{ cursor: "pointer" }}
                          onClick={() => toggleMonthExpansion(month)}
                        >
                          <CardHeader className="bg-light">
                            <Row className="align-items-center">
                              <Col>
                                <h5 className="mb-0">
                                  {month} ({totalTransactions} transactions)
                                </h5>
                                <small className="text-muted">
                                  Total Amount: {getAmt(totalAmount)}
                                </small>
                              </Col>
                              <Col className="col-auto">
                                <i
                                  className={`fas fa-chevron-${
                                    isExpanded ? "up" : "down"
                                  }`}
                                />
                              </Col>
                            </Row>
                          </CardHeader>
                        </Card>

                        <Collapse isOpen={isExpanded}>
                          <Card className="shadow-sm">
                            <CardBody>
                              <Table
                                className="align-items-center table-flush"
                                responsive
                              >
                                <thead className="thead-light">
                                  <tr>
                                    <th scope="col">Transaction ID</th>
                                    <th scope="col">Account Number</th>
                                    <th scope="col">Name</th>
                                    <th scope="col">Product</th>
                                    <th scope="col">Amount</th>
                                    <th scope="col">Wallet Number</th>
                                    <th scope="col">Status</th>
                                    <th scope="col">Date</th>
                                    <th scope="col">Log By</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {monthData.map((transaction, index) => (
                                    <tr key={index}>
                                      <td>{transaction.transactionId}</td>
                                      <td>{transaction.accountNumber}</td>
                                      <td>{transaction.name}</td>
                                      <td>{transaction.product}</td>
                                      <td>
                                        {getAmt(transaction.transferedAmount)}
                                      </td>
                                      <td>{transaction.walletNumber}</td>
                                      <td>
                                        {getStatusBadge(transaction.status)}
                                      </td>
                                      <td>
                                        {moment(transaction.logDate).format(
                                          "DD/MM/YYYY HH:mm"
                                        )}
                                      </td>
                                      <td>{transaction.logBy}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </Table>
                            </CardBody>
                          </Card>
                        </Collapse>
                      </div>
                    );
                  })}
                </div>

                {loading && <FullPageLoader label="Loading..." />}

                {!loading && reportData.length === 0 && <NoRecordFound />}
              </CardBody>
            </Card>
          </div>
        </Row>
      </Container>
    </>
  );
};

export default DisbursementReport;
